import { useState, useRef, useEffect } from "react";
import { api } from "@/lib/api";
import { useWeek } from "@/context/WeekContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UploadCloud, CheckCircle2, AlertTriangle, FileSpreadsheet, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function UploadPage() {
  const { refresh, setWeek, week } = useWeek();
  const [file, setFile] = useState(null);
  const [weekLabel, setWeekLabel] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [uploads, setUploads] = useState([]);
  const inputRef = useRef(null);

  const loadUploads = async () => {
    const { data } = await api.get("/uploads");
    setUploads(data || []);
  };

  useEffect(() => { loadUploads(); }, []);

  const deleteWeek = async (wl) => {
    if (!window.confirm(`Delete all complaints for week ${wl}? This cannot be undone.`)) return;
    try {
      const { data } = await api.delete(`/uploads/${encodeURIComponent(wl)}`);
      toast.success(`Deleted ${data.complaints_deleted} complaints for ${wl}`);
      await loadUploads();
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const doUpload = async () => {
    if (!file) { toast.error("Choose a file first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const url = weekLabel ? `/uploads?week_label=${encodeURIComponent(weekLabel)}` : "/uploads";
      const { data } = await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      await refresh();
      await loadUploads();
      setWeek(data.week_label);
      toast.success(`Ingested ${data.matched_rows} complaints for ${data.week_label}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    if (e.dataTransfer.files?.[0]) setFile(e.dataTransfer.files[0]);
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">Upload Weekly File</h1>
        <p className="text-sm text-slate-500 mt-1">Drop a .xlsx / .xls / .csv RailMADAD export. Columns auto-detected.</p>
      </div>

      <Card className="p-6 border border-slate-200">
        <div
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          data-testid="upload-dropzone"
          className={`border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition-colors ${
            drag ? "border-blue-500 bg-blue-50" : "border-slate-300 hover:border-slate-400 bg-slate-50/50"
          }`}
        >
          <UploadCloud className="h-10 w-10 text-slate-400 mx-auto mb-3" />
          <div className="font-semibold text-slate-700">
            {file ? file.name : "Drop file or click to browse"}
          </div>
          <div className="text-xs text-slate-500 mt-1">Accepts .xlsx, .xls, .csv</div>
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            data-testid="upload-file-input"
            onChange={(e) => setFile(e.target.files?.[0])}
          />
        </div>

        <div className="mt-5 flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs uppercase font-semibold tracking-wider text-slate-500">Week label (optional)</Label>
            <Input
              data-testid="week-label-input"
              value={weekLabel}
              onChange={(e) => setWeekLabel(e.target.value)}
              placeholder="auto (e.g. 2026-W05)"
              className="h-9 mt-1 w-56 font-mono"
            />
          </div>
          <Button
            data-testid="upload-excel-btn"
            onClick={doUpload}
            disabled={!file || busy}
            className="bg-blue-600 hover:bg-blue-500 text-white h-9"
          >
            <FileSpreadsheet className="h-4 w-4 mr-1.5" />
            {busy ? "Processing…" : "Upload & Parse"}
          </Button>
        </div>
      </Card>

      {result && (
        <Card className="p-6 border border-slate-200">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            <div className="font-display font-semibold text-lg">Ingest complete — {result.week_label}</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatBox label="Total Rows" value={result.total_rows} />
            <StatBox label="Matched" value={result.matched_rows} tone="success" />
            <StatBox label="Unmatched" value={result.unmatched_rows} tone="warning" />
            <StatBox label="Ignored (non-4-head)" value={result.ignored_rows} tone="muted" />
          </div>
          {Object.keys(result.ignored_head_counts || {}).length > 0 && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-800 mb-1.5">
                <AlertTriangle className="h-3.5 w-3.5" /> Ignored heads found (not counted):
              </div>
              <div className="flex flex-wrap gap-2 text-xs font-mono">
                {Object.entries(result.ignored_head_counts).map(([k, v]) => (
                  <span key={k} className="px-2 py-0.5 rounded bg-white border border-amber-200 text-amber-800">
                    {k} <span className="font-bold">{v}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="mt-4 text-xs font-mono text-slate-500">
            Detected columns: {JSON.stringify(result.detected_columns)}
          </div>
        </Card>
      )}

      <Card className="border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 bg-slate-100 border-b border-slate-200 text-xs uppercase font-semibold tracking-wider text-slate-600">
          Uploaded Weeks ({uploads.length})
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-white text-[11px] uppercase tracking-wider text-slate-500">
              <th className="text-left px-4 py-2">Week</th>
              <th className="text-left px-4 py-2">File</th>
              <th className="text-right px-4 py-2">Rows</th>
              <th className="text-right px-4 py-2">Matched</th>
              <th className="text-right px-4 py-2">Unmatched</th>
              <th className="text-right px-4 py-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {uploads.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-sm text-slate-400">No uploads yet</td></tr>}
            {uploads.map((u) => (
              <tr key={u.week_label} data-testid={`upload-row-${u.week_label}`} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-2.5 font-mono font-semibold">{u.week_label}</td>
                <td className="px-4 py-2.5 text-xs text-slate-600 max-w-[220px] truncate">{u.filename}</td>
                <td className="px-4 py-2.5 text-right font-mono">{u.total_rows}</td>
                <td className="px-4 py-2.5 text-right font-mono text-emerald-700">{u.matched_rows}</td>
                <td className="px-4 py-2.5 text-right font-mono text-amber-700">{u.unmatched_rows}</td>
                <td className="px-4 py-2.5 text-right">
                  <Button
                    data-testid={`delete-week-${u.week_label}`}
                    size="sm"
                    variant="ghost"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => deleteWeek(u.week_label)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

const StatBox = ({ label, value, tone = "default" }) => {
  const cls = {
    default: "border-slate-200 bg-white text-slate-900",
    success: "border-emerald-200 bg-emerald-50 text-emerald-800",
    warning: "border-amber-200 bg-amber-50 text-amber-800",
    muted: "border-slate-200 bg-slate-50 text-slate-600",
  }[tone];
  return (
    <div className={`p-3 rounded-lg border ${cls}`}>
      <div className="text-[10px] uppercase font-semibold tracking-wider opacity-70">{label}</div>
      <div className="font-display font-bold text-2xl mt-0.5">{value}</div>
    </div>
  );
};
